import dayjs from '../utils/localDayjs'
import {DateObject} from 'react-multi-date-picker'

/**
 * Convertit une liste de dates (string[]) de l'API en DateObject[][] pour le composant calendrier.
 * Groupe automatiquement les dates contiguës en plages.
 */
export function apiDatesToCalendarRanges(dates: string[]): DateObject[][] {
  if (!Array.isArray(dates) || dates.length === 0) return []

  // Grouper en plages de dates contiguës
  const uniqueSorted = Array.from(new Set(dates)).sort()
  const ranges: string[][] = []
  let rangeStart = uniqueSorted[0]
  let prev = rangeStart

  for (let i = 1; i < uniqueSorted.length; i++) {
    const cur = uniqueSorted[i]
    const expectedNext = dayjs(prev).add(1, 'day').format('YYYY-MM-DD')
    if (cur === expectedNext) {
      prev = cur
      continue
    }
    // Fermer plage actuelle
    ranges.push(rangeStart === prev ? [rangeStart] : [rangeStart, prev])
    rangeStart = cur
    prev = cur
  }
  // Dernière plage
  ranges.push(rangeStart === prev ? [rangeStart] : [rangeStart, prev])

  // Convertir en DateObject[][]
  return ranges.map(range =>
    range.map(dateStr => {
      const parsed = dayjs(dateStr)
      return new DateObject({
        year: parsed.year(),
        month: parsed.month() + 1,
        day: parsed.date(),
      })
    }),
  )
}

/**
 * Convertit DateObject[][] du composant calendrier en string[] pour l'API.
 * Aplati toutes les plages en liste de dates individuelles.
 */
export function calendarRangesToApiDates(ranges: DateObject[][]): string[] {
  if (!Array.isArray(ranges) || ranges.length === 0) return []

  const allDates: string[] = []

  for (const range of ranges) {
    if (!Array.isArray(range) || range.length === 0) continue

    const start = range[0]
    const end = range.length > 1 ? range[1] : range[0]

    // Convertir DateObject en dayjs pour itération
    let cur = dayjs()
      .year(start.year)
      .month(start.month.number - 1)
      .date(start.day)
    const last = dayjs()
      .year(end.year)
      .month(end.month.number - 1)
      .date(end.day)

    while (cur.isBefore(last) || cur.isSame(last, 'day')) {
      allDates.push(cur.format('YYYY-MM-DD'))
      cur = cur.add(1, 'day')
    }
  }

  return Array.from(new Set(allDates)).sort()
}

/**
 * Calcule min/max des dates à partir de DateObject[][].
 * Retourne {minDate, maxDate} en format YYYY-MM-DD.
 */
export function getMinMaxFromCalendarRanges(ranges: DateObject[][]): {
  minDate?: string
  maxDate?: string
} {
  if (!Array.isArray(ranges) || ranges.length === 0) return {}

  const allDates = calendarRangesToApiDates(ranges)
  if (allDates.length === 0) return {}

  return {
    minDate: allDates[0], // déjà trié
    maxDate: allDates[allDates.length - 1],
  }
}
